import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import type { PresetRef } from "../contract/types";
import {
  ApiError,
  createSession,
  getSession,
  runStreamUrl,
  sendMessage,
  type RunStarted,
  type SessionDetail,
} from "../api/client";
import { useBlockAction } from "../blocks/useBlockAction";
import {
  initialStreamState,
  parseStreamEvent,
  streamReducer,
  type RunStatus,
  type Turn,
} from "./reducer";

/**
 * Cliente SSE + estado de la conversación.
 *
 * Se usa `fetch` con `ReadableStream` en lugar de `EventSource` porque
 * necesitamos control fino sobre tres cosas que `EventSource` no da:
 *   - cortar la reconexión automática cuando el run ya terminó,
 *   - abortar el stream a voluntad (botón "Detener"),
 *   - reintentar con backoff propio sabiendo que el run se reenvía entero.
 *
 * El backend reproduce el run desde el principio en cada conexión, así que un
 * reintento empieza limpiando el turno en curso y volviendo a aplicarlo: el
 * resultado es idempotente.
 */

const MAX_RECONNECT_ATTEMPTS = 4;
const BASE_BACKOFF_MS = 400;
const MAX_BACKOFF_MS = 4_000;

export type BootState = "loading" | "ready" | "offline";
export type ConnectionState = "idle" | "connecting" | "open" | "retrying" | "closed";

export interface RunController {
  sessionId: string | null;
  boot: BootState;
  connection: ConnectionState;
  turns: Turn[];
  status: RunStatus;
  preset: PresetRef | null;
  isStreaming: boolean;
  lastError: string | null;
  send: (content: string) => Promise<boolean>;
  invokeAction: (blockId: string, event: string, payload: Record<string, unknown>) => Promise<boolean>;
  stop: () => void;
  openPreset: (preset: PresetRef) => void;
  closePreset: () => void;
  retryBoot: () => void;
}

/* -------------------------------------------------------------------------- */
/* Parser SSE                                                                 */
/* -------------------------------------------------------------------------- */

interface SseFrame {
  name: string;
  data: string;
}

function splitFrames(buffer: string): { frames: SseFrame[]; rest: string } {
  const normalized = buffer.replace(/\r\n/g, "\n");
  const chunks = normalized.split("\n\n");
  const rest = chunks.pop() ?? "";
  const frames: SseFrame[] = [];

  for (const chunk of chunks) {
    if (chunk.trim() === "") continue;
    let name = "message";
    const dataLines: string[] = [];
    for (const line of chunk.split("\n")) {
      if (line.startsWith(":")) continue; // comentario / heartbeat
      if (line.startsWith("event:")) name = line.slice(6).trim();
      else if (line.startsWith("data:")) dataLines.push(line.slice(5).replace(/^ /, ""));
    }
    if (dataLines.length > 0) frames.push({ name, data: dataLines.join("\n") });
  }

  return { frames, rest };
}

async function pumpStream(
  url: string,
  signal: AbortSignal,
  onFrame: (frame: SseFrame) => void,
): Promise<void> {
  const response = await fetch(url, {
    method: "GET",
    headers: { Accept: "text/event-stream" },
    cache: "no-store",
    signal,
  });

  if (!response.ok) {
    throw new ApiError(`El stream respondió ${response.status}`, response.status, "stream_http_error");
  }
  if (!response.body) {
    throw new ApiError("El stream no trae cuerpo", 500, "stream_no_body");
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const { frames, rest } = splitFrames(buffer);
      buffer = rest;
      for (const frame of frames) onFrame(frame);
    }
    const tail = buffer + decoder.decode();
    if (tail.trim() !== "") {
      for (const frame of splitFrames(`${tail}\n\n`).frames) onFrame(frame);
    }
  } finally {
    reader.releaseLock();
  }
}

function delay(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(new DOMException("Aborted", "AbortError"));
      return;
    }
    const timer = window.setTimeout(() => {
      signal.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    function onAbort() {
      window.clearTimeout(timer);
      reject(new DOMException("Aborted", "AbortError"));
    }
    signal.addEventListener("abort", onAbort, { once: true });
  });
}

function backoffMs(attempt: number): number {
  return Math.min(BASE_BACKOFF_MS * 2 ** (attempt - 1), MAX_BACKOFF_MS);
}

function localId(prefix: string): string {
  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
}

/* -------------------------------------------------------------------------- */
/* Sesión recordada                                                           */
/* -------------------------------------------------------------------------- */

const SESSION_KEY = "course-agent.session-id";

/**
 * El historial vive en el servidor, pero el navegador es quien sabe CUÁL es su
 * sesión. Sin recordarla, recargar la página crea una sesión nueva y deja la
 * anterior inalcanzable: el estudiante pierde el hilo de la conversación.
 *
 * `localStorage` puede lanzar (ventana privada, almacenamiento bloqueado), así
 * que todo acceso va envuelto y la app funciona igual sin él.
 */
function readStoredSession(): string | null {
  try {
    return window.localStorage.getItem(SESSION_KEY);
  } catch {
    return null;
  }
}

function storeSession(sessionId: string): void {
  try {
    window.localStorage.setItem(SESSION_KEY, sessionId);
  } catch {
    /* Sin almacenamiento: se sigue con la sesión en memoria. */
  }
}

function clearStoredSession(): void {
  try {
    window.localStorage.removeItem(SESSION_KEY);
  } catch {
    /* nada que limpiar */
  }
}

/**
 * Recupera una sesión guardada. Devuelve `null` si el servidor ya no la
 * conoce —base reiniciada, sesión borrada—, que es un caso normal y no un
 * error: quien llama simplemente abre una sesión nueva.
 */
async function restoreSession(sessionId: string): Promise<SessionDetail | null> {
  try {
    return await getSession(sessionId);
  } catch {
    return null;
  }
}

/* -------------------------------------------------------------------------- */
/* Hook                                                                       */
/* -------------------------------------------------------------------------- */

export function useRun(): RunController {
  const [state, dispatch] = useReducer(streamReducer, initialStreamState);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [boot, setBoot] = useState<BootState>("loading");
  const [bootNonce, setBootNonce] = useState(0);
  const [connection, setConnection] = useState<ConnectionState>("idle");
  const [lastError, setLastError] = useState<string | null>(null);

  const sendingRef = useRef(false);
  const [isSending, setIsSending] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const connectedRunRef = useRef<string | null>(null);

  /* --- Arranque de sesión ------------------------------------------------ */
  useEffect(() => {
    let cancelled = false;
    setBoot("loading");
    setLastError(null);

    void (async () => {
      try {
        // Primero, reanudar la conversación anterior si este navegador la
        // recuerda. Solo si no hay ninguna —o si el servidor ya no la tiene,
        // porque se reinició la base— se abre una sesión nueva.
        const saved = readStoredSession();
        if (saved) {
          const detail = await restoreSession(saved);
          if (cancelled) return;
          if (detail) {
            setSessionId(saved);
            dispatch({ type: "session/loaded", messages: detail.messages });
            setBoot("ready");
            return;
          }
          clearStoredSession();
        }

        const id = await createSession();
        if (cancelled) return;
        storeSession(id);
        setSessionId(id);
        setBoot("ready");
        try {
          const detail = await getSession(id);
          if (!cancelled) dispatch({ type: "session/loaded", messages: detail.messages });
        } catch {
          /* Historial no disponible: se sigue con la conversación vacía. */
        }
      } catch (error) {
        if (cancelled) return;
        setBoot("offline");
        setLastError(
          error instanceof ApiError ? error.message : "Sin conexión con el backend.",
        );
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [bootNonce]);

  /* --- Conexión al stream del run activo --------------------------------- */
  useEffect(() => {
    const runId = state.activeRunId;
    if (!runId || !sessionId) return;
    if (connectedRunRef.current === runId) return;
    connectedRunRef.current = runId;

    const controller = new AbortController();
    abortRef.current = controller;
    const url = runStreamUrl(sessionId, runId);
    let finished = false;
    let attempt = 0;

    const handleFrame = (frame: SseFrame) => {
      let payload: unknown = null;
      try {
        payload = frame.data ? JSON.parse(frame.data) : null;
      } catch {
        // Un data no-JSON no debe tumbar el stream: se ignora esa trama.
        return;
      }
      const event = parseStreamEvent(frame.name, payload);
      if (event.kind === "run.done" || event.kind === "error") finished = true;
      dispatch({ type: "stream/event", event });
      if (event.kind === "error") setLastError(event.data.message);
    };

    void (async () => {
      for (;;) {
        if (controller.signal.aborted) return;
        try {
          setConnection(attempt === 0 ? "connecting" : "retrying");
          if (attempt > 0) dispatch({ type: "run/replay", runId });
          await pumpStream(url, controller.signal, handleFrame);
          if (controller.signal.aborted) return;
          setConnection("open");
          if (finished) {
            connectedRunRef.current = null;
            setConnection("closed");
            return;
          }
          attempt += 1;
          if (attempt > MAX_RECONNECT_ATTEMPTS) {
            dispatch({
              type: "stream/interrupted",
              message: "La conexión con el stream se perdió y no se pudo restablecer.",
            });
            setConnection("closed");
            return;
          }
          await delay(backoffMs(attempt), controller.signal);
        } catch (error) {
          if (controller.signal.aborted) return;
          if (error instanceof DOMException && error.name === "AbortError") return;
          attempt += 1;
          if (attempt > MAX_RECONNECT_ATTEMPTS) {
            dispatch({
              type: "stream/interrupted",
              message:
                error instanceof Error
                  ? `No se pudo abrir el stream: ${error.message}`
                  : "No se pudo abrir el stream.",
            });
            setConnection("closed");
            return;
          }
          try {
            await delay(backoffMs(attempt), controller.signal);
          } catch {
            return;
          }
        }
      }
    })();

    return () => {
      controller.abort();
      if (abortRef.current === controller) abortRef.current = null;
    };
  }, [state.activeRunId, sessionId]);

  /* --- Acciones ---------------------------------------------------------- */

  const beginRun = useCallback((started: RunStarted, createdAt: string) => {
    connectedRunRef.current = null;
    dispatch({
      type: "run/prepare",
      runId: started.run_id,
      messageId: started.message_id,
      createdAt,
    });
  }, []);

  // El POST de acciones vive en su propio hook: el mismo round-trip vale para
  // un quiz, un simulador o un BlockAction, y aquí solo se encadena el stream.
  const blockActions = useBlockAction(
    sessionId,
    useCallback(
      (started: RunStarted) => beginRun(started, new Date().toISOString()),
      [beginRun],
    ),
  );

  const send = useCallback(
    async (content: string): Promise<boolean> => {
      const trimmed = content.trim();
      if (trimmed === "" || sendingRef.current || state.status === "streaming" || state.status === "connecting") return false;
      if (!sessionId) {
        setLastError("Todavía no hay sesión: el backend no está disponible.");
        return false;
      }
      sendingRef.current = true;
      setIsSending(true);
      setLastError(null);
      dispatch({
        type: "user/append",
        id: localId("usr"),
        content: trimmed,
        createdAt: new Date().toISOString(),
      });
      try {
        const started = await sendMessage(sessionId, trimmed);
        beginRun(started, new Date().toISOString());
        return true;
      } catch (error) {
        dispatch({
          type: "run/failed",
          message:
            error instanceof ApiError
              ? `No se pudo enviar el mensaje: ${error.message}`
              : "No se pudo enviar el mensaje.",
          createdAt: new Date().toISOString(),
        });
        return false;
      } finally {
        sendingRef.current = false;
        setIsSending(false);
      }
    },
    [beginRun, sessionId, state.status],
  );

  const invokeAction = useCallback(
    async (blockId: string, event: string, payload: Record<string, unknown>): Promise<boolean> => {
      const started = await blockActions.invoke(blockId, event, payload);
      return started !== null;
    },
    [blockActions],
  );

  const stop = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    connectedRunRef.current = null;
    setConnection("closed");
    dispatch({ type: "stream/stopped" });
  }, []);

  const openPreset = useCallback((preset: PresetRef) => dispatch({ type: "preset/open", preset }), []);
  const closePreset = useCallback(() => dispatch({ type: "preset/close" }), []);
  const retryBoot = useCallback(() => setBootNonce((nonce) => nonce + 1), []);

  const isStreaming = isSending || state.status === "streaming" || state.status === "connecting";

  // Si un preset abre a mitad de stream, el turno sigue su curso: la StageView
  // lo recupera al volver, porque el estado vive aquí arriba.
  const turns = useMemo(() => state.turns, [state.turns]);

  return {
    sessionId,
    boot,
    connection,
    turns,
    status: state.status,
    preset: state.preset,
    isStreaming,
    lastError,
    send,
    invokeAction,
    stop,
    openPreset,
    closePreset,
    retryBoot,
  };
}
